import { LoadableCentered } from "common/Loadable";
import { Popup } from "common/Map/Popup";
import { MapV2 } from "common/MapV2";
import { FullscreenButton } from "common/MapV2/controls/FullscreenButton";
import { TrackLine } from "common/MapV2/TrackLine";
import { Topo } from "common/styleguide/Topo";
import { Distance, Elevation } from "common/units";
import { Viewport } from "data/records";
import { routeSelectors } from "data/resources/routes";
import { routeSetOverviewActions } from "data/resources/routeSetOverviews";
import { tripSelectors } from "data/resources/trips";
import { ROUTE_COLORS } from "features/embed/RegionalEmbed/RegionalEmbedlet";
import { Map } from "immutable";
import { loadGeoJSON } from "lib/api/embeds/regional";
import { northernmostPoint } from "lib/geoUtils";
import preloadImg from "lib/preloadImg";
import { photoMedium } from "lib/urlHelpers";
import { urls } from "lib/urls";
import { capitalize, clampStringWords } from "lib/utils";
import _ from "lodash";
import React from "react";
import { FormattedMessage as T } from "react-intl";
import { connect } from "react-redux";
import { Link } from "react-router-dom";
import styles from "./CollectionMap.scss";
import mobileMap from "./mobileMap.png";

export class _CollectionMap extends React.PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      dataLoaded: false,
      dataLoadFailures: 0,
      geoJSON: null,
      geoJSONFeatureMap: Map(),
      geoJSONParsed: false,
      selectedFeature: null,
      hoveredFeatureId: null,
      showMobileMap: false,
    };
  }

  componentDidMount() {
    window.getBounds = this.getBounds;
    this.preloadImages();
    this.loadData();
  }

  componentDidUpdate(prevState, prevProps) {
    const { fullscreen } = this.props;
    if (!prevProps.fullscreen && fullscreen) {
      this._map._mapEngine.requestFullscreen();
      this._map._mapEngine.resizeToParentHeight();
    }
    if (prevProps.fullscreen && !fullscreen) {
      document.exitFullscreen();
    }
  }

  async loadData() {
    const { loadRouteSetOverview, routeSetOverview, updatedAt, loadGeoJSON } = this.props;
    const { dataLoadFailures } = this.state;
    try {
      const response = await loadGeoJSON(routeSetOverview.id, updatedAt);
      if (dataLoadFailures > 0) {
        await new Promise((resolve) => setTimeout(resolve, 3000));
        await loadRouteSetOverview(routeSetOverview.id);
      }
      this.setState({
        geoJSON: response.action.payload,
        dataLoaded: true,
      });
    } catch (e) {
      // when you first create a collection, the map can take a little time to generate.
      // try every 2 seconds for 2 minutes, and if it's not coming up, stop trying.
      if (dataLoadFailures < 60) {
        this.setState({ dataLoadFailures: dataLoadFailures + 1 });
        setTimeout(() => this.loadData(), 2000);
      }
    }
  }

  preloadImages() {
    const { routeSetOverview, findTrip, findRoute } = this.props;

    // routeIds and tripIds are both present, or both null (before any troutes are added)
    if (!routeSetOverview || !routeSetOverview.routeIds) return;

    routeSetOverview.routeIds.forEach((id) => {
      const route = findRoute(id);
      if (!route) {
        console.warn("could not find route metadata for route " + id);
        return;
      }
      const pid = route.highlightedPhotoId;
      if (!pid) {
        return;
      }
      preloadImg(photoMedium(pid));
    });

    routeSetOverview.tripIds.forEach((id) => {
      const trip = findTrip(id);
      if (!trip) {
        console.warn("could not find trip metadata for trip " + id);
        return;
      }
      const pid = trip.highlightedPhotoId;
      if (!pid) {
        return;
      }
      preloadImg(photoMedium(pid));
    });
  }

  parseIntoViewport(bbStr) {
    if (!bbStr || bbStr === ",,,") {
      return undefined;
    }

    const boundsArr = bbStr.split(",").map(parseFloat);
    return Viewport({
      bounds: [
        {
          lng: boundsArr[0],
          lat: boundsArr[1],
        },
        {
          lng: boundsArr[2],
          lat: boundsArr[3],
        },
      ],
    });
  }

  onMouseEnterTrackLine = (featureId) => {
    const m = this._map._mapEngine._map;
    m.queryRenderedFeatures().forEach((f) => {
      f.layer.id.slice(0, f.layer.id.length - 1) === featureId.slice(0, featureId.length - 1)
        ? m.setPaintProperty(featureId, "line-opacity", 1)
        : m.setPaintProperty(f.layer.id, "line-opacity", 0.5);
    });
  };

  onMouseLeaveTrackLine = () => {
    const m = this._map._mapEngine._map;
    m.queryRenderedFeatures().forEach((f) => {
      m.setPaintProperty(f.layer.id, "line-opacity", 1);
    });
  };

  // geoJSON feature geometry is an array of arrays, <TrackLine /> wants {lat, lng}
  trackPointsFromGeoJSON = _.memoize((features) => {
    let map = Map();
    let featureMap = Map({ trip: [], route: [] });
    features.forEach((f) => {
      map = map.setIn(
        [f.properties.type, f.properties.id],
        f.geometry.coordinates.map((c) => ({ lat: c[1], lng: c[0] }))
      );
      featureMap = featureMap.set(f.properties.type, featureMap.get(f.properties.type).concat(f.properties.id));
    });
    this.setState({ geoJSONFeatureMap: featureMap, geoJSONParsed: true });
    return map;
  });

  // verify that a feature is both associated with the collection in redux, and is
  // included in routeSetOverview assets
  isAnAsset(f) {
    const { collectionItems, collectionId, routeSetOverview } = this.props;
    const { id, type } = f.properties;
    const collectionItem = collectionItems.find((ci) => ci.itemType === type && ci.itemId === id);

    const idsProperty = { trip: "tripIds", route: "routeIds" }[type];
    return (
      !!routeSetOverview[idsProperty].includes(id) && collectionItem && collectionItem.collectionId === collectionId
    );
  }

  nonGeoJSONTRoutes() {
    const { collectionItems, findTrip, findRoute, readonly } = this.props;
    const { geoJSONFeatureMap, geoJSONParsed } = this.state;

    // `readonly` in this context means that the user is not given the
    // opportunity to add items to the map on this page (ie, we don't have to
    // support troutes that aren't in geoJSON)
    if (readonly) {
      return [];
    }

    return collectionItems
      .filter((i) => !geoJSONParsed || !geoJSONFeatureMap.get(i.itemType).includes(i.itemId))
      .valueSeq()
      .map((i) => (i.itemType === "route" ? findRoute(i.itemId) : findTrip(i.itemId)));
  }

  renderGeoJSONFeatures() {
    const { readonly } = this.props;
    const { geoJSON } = this.state;
    if (!geoJSON.features) return;
    const tpMap = this.trackPointsFromGeoJSON(geoJSON.features);

    // `readonly` in this context means that the user is not given the
    // opportunity to add items to the map on this page (ie, we don't have to
    // support troutes that aren't in geoJSON)
    const geoJSONFeatures = readonly ? geoJSON.features : geoJSON.features.filter((f) => this.isAnAsset(f));
    return geoJSONFeatures.map((f, idx) => (
      <React.Fragment key={`${f.properties.type}-${f.properties.id}`}>
        <TrackLine
          weight={7}
          color="#FFF"
          showStartMarker={false}
          showEndMarker={false}
          trackPoints={tpMap.getIn([f.properties.type, f.properties.id])}
        />
        <TrackLine
          color={ROUTE_COLORS[idx % ROUTE_COLORS.length]}
          showStartMarker={false}
          showEndMarker={false}
          onMouseEnter={this.onMouseEnterTrackLine}
          onMouseLeave={this.onMouseLeaveTrackLine}
          onClick={() =>
            this.setState({
              selectedFeature: { type: f.properties.type, id: f.properties.id },
            })
          }
          trackPoints={tpMap.getIn([f.properties.type, f.properties.id])}
        />
      </React.Fragment>
    ));
  }

  renderNonGeoJSONFeatures() {
    return this.nonGeoJSONTRoutes().map((troute, idx) => {
      const modelKey = troute.has("duration") ? "trip" : "route";
      return (
        <React.Fragment key={`${modelKey}-${troute.id}`}>
          <TrackLine
            weight={7}
            color="#FFF"
            showStartMarker={false}
            showEndMarker={false}
            trackPoints={troute.trackPoints}
          />
          <TrackLine
            color={ROUTE_COLORS[(idx % ROUTE_COLORS.length) - 1]}
            showStartMarker={false}
            showEndMarker={false}
            onMouseEnter={this.onMouseEnterTrackLine}
            onMouseLeave={this.onMouseLeaveTrackLine}
            onClick={() =>
              this.setState({
                selectedFeature: { type: modelKey, id: troute.id },
              })
            }
            trackPoints={troute.trackPoints}
          />
        </React.Fragment>
      );
    });
  }

  renderSelectedFeature() {
    const { geoJSON, geoJSONFeatureMap, selectedFeature } = this.state;
    if (!selectedFeature) {
      return null;
    }
    const { findTrip, findRoute } = this.props;
    let troute, url;
    if (selectedFeature.type === "route") {
      troute = findRoute(selectedFeature.id);
      url = urls.routes.show(troute.id, troute.privacyCode);
    } else {
      troute = findTrip(selectedFeature.id);
      url = urls.trips.show(troute.id, troute.privacyCode);
    }

    // depending on what feature is selected, we need to get trackPoints from geoJSON or redux
    const position =
      geoJSONFeatureMap.get(selectedFeature.type).indexOf(selectedFeature.id) >= 0
        ? northernmostPoint(
            this.trackPointsFromGeoJSON(geoJSON.features).getIn([selectedFeature.type, selectedFeature.id])
          )
        : northernmostPoint(troute.trackPoints);

    return (
      <Popup
        autoPan
        noPadding
        closeButton
        onRequestClose={() => this.setState({ selectedFeature: null })}
        footer={
          <Link to={url} className={styles.footerLink}>
            View {capitalize(selectedFeature.type)}
          </Link>
        }
        position={position}
      >
        <div className={styles.wrap}>
          {troute.highlightedPhotoId > 0 && (
            <div className={styles.photoWrap}>
              <Link to={url}>
                <img src={photoMedium(troute.highlightedPhotoId)} />
              </Link>
            </div>
          )}
          <div className={styles.rest}>
            <h3>
              <Link to={url}>{troute.name}</Link>
            </h3>
            <p className={styles.small}>
              <span className={styles.metric}>
                <Distance short value={troute.distance} />
              </span>
              <span className={styles.metric}>
                +<Elevation short value={troute.elevationGain} />
              </span>
            </p>
            <p>{clampStringWords(troute.description || "", 140)}</p>
          </div>
        </div>
      </Popup>
    );
  }

  onMapClick = ({ e, feature }) => {
    !feature && this.setState({ selectedFeature: null });
  };

  setShowMobileMap(bool) {
    this.setState({ showMobileMap: bool });
  }

  render() {
    const { collapse, routeSetOverview } = this.props;
    const { dataLoaded, showMobileMap } = this.state;

    if (!dataLoaded || !routeSetOverview.get("embedSettings")) {
      return (
        <LoadableCentered
          loading
          loadingText={<T id="CollectionMap.loadableText" defaultMessage="Loading your map" />}
        />
      );
    }

    const vp = this.parseIntoViewport(routeSetOverview.get("embedSettings")["bounding_box"]);

    if (collapse) {
      if (showMobileMap) {
        return (
          <Topo dark className={styles.mobileMap}>
            <MapV2
              ref={(r) => {
                this._map = r;
              }}
              hideOverlayPicker
              onMapClick={this.onMapClick}
              initialViewport={vp}
              controls={<FullscreenButton isFullscreen onClick={() => this.setShowMobileMap(false)} />}
            >
              {this.renderGeoJSONFeatures()}
              {this.renderNonGeoJSONFeatures()}
              {this.renderSelectedFeature()}
            </MapV2>
          </Topo>
        );
      } else {
        return (
          <div className={styles.mobileMapPlaceholder} style={{ backgroundImage: `url(${mobileMap})` }}>
            <div className={styles.scrim} onClick={() => this.setShowMobileMap(true)}>
              View Map
            </div>
          </div>
        );
      }
    }

    return (
      <MapV2
        scrollWheelZoom={false}
        ref={(r) => {
          this._map = r;
        }}
        hideOverlayPicker
        onMapClick={this.onMapClick}
        initialViewport={vp}
      >
        {this.renderGeoJSONFeatures()}
        {this.renderNonGeoJSONFeatures()}
        {this.renderSelectedFeature()}
      </MapV2>
    );
  }
}

const mapStateToProps = (state, ownProps) => {
  return {
    findRoute: (id) => routeSelectors.find(state, id),
    findTrip: (id) => tripSelectors.find(state, id),
  };
};

const mapDispatchToProps = {
  loadGeoJSON,
  loadRouteSetOverview: routeSetOverviewActions.read,
};

export const CollectionMap = connect(mapStateToProps, mapDispatchToProps)(_CollectionMap);
